import React from 'react'
import {ListGroup, Badge} from 'react-bootstrap'
import {FormattedMessage} from 'react-intl'
import {RecipientFilters as FilterTypes, RecipientStats} from '../recipientsTypes'
import {getTagContrastColor} from '../../../helpers'
import {RECIPIENT_MESSAGES as M} from '../recipientMessages'
import './RecipientFilters.css'

interface ContactTag {
  id: string
  name: string
  color: string
  contactCount: number
}

interface ContactSegment {
  id: string
  name: string
  contactCount: number
}

interface RecipientFiltersProps {
  filters: FilterTypes
  onFilterChange: (filters: Partial<FilterTypes>) => void
  stats?: RecipientStats
  tags?: ContactTag[]
  segments?: ContactSegment[]
}

export const RecipientFilters: React.FC<RecipientFiltersProps> = ({
  filters,
  onFilterChange,
  stats = {total: 0, subscribed: 0, unsubscribed: 0},
  tags = [],
  segments = [],
}) => {
  const handleTagClick = (tagId: string) => {
    const current = filters.tags || []
    const updated = current.includes(tagId)
      ? current.filter((t) => t !== tagId)
      : [...current, tagId]
    onFilterChange({tags: updated})
  }

  const handleSegmentClick = (segmentId: string) => {
    onFilterChange({segment: filters.segment === segmentId ? '' : segmentId})
  }

  const hasActiveFilters =
    filters.status !== 'ALL' ||
    filters.search !== '' ||
    (filters.tags || []).length > 0 ||
    !!filters.segment ||
    (filters.effectiveYears || []).length > 0

  return (
    <div className='recipient-filters'>
      <div className='filter-section'>
        <h6 className='filter-title'><FormattedMessage {...M.filtersTitle} /></h6>

        {/* Status Filter */}
        <ListGroup variant='flush' className='mb-4'>
          <ListGroup.Item
            action
            active={filters.status === 'ALL'}
            onClick={() => onFilterChange({status: 'ALL'})}
            className='d-flex justify-content-between align-items-center'
          >
            <div>
              <i className='bi bi-people me-2'></i>
              <FormattedMessage {...M.allRecipients} />
            </div>
            <Badge bg='secondary'>{stats.total}</Badge>
          </ListGroup.Item>

          <ListGroup.Item
            action
            active={filters.status === 'SUBSCRIBED'}
            onClick={() => onFilterChange({status: 'SUBSCRIBED'})}
            className='d-flex justify-content-between align-items-center'
          >
            <div>
              <i className='bi bi-check-circle me-2'></i>
              <FormattedMessage {...M.subscribed} />
            </div>
            <Badge bg='success'>{stats.subscribed}</Badge>
          </ListGroup.Item>

          <ListGroup.Item
            action
            active={filters.status === 'UNSUBSCRIBED'}
            onClick={() => onFilterChange({status: 'UNSUBSCRIBED'})}
            className='d-flex justify-content-between align-items-center'
          >
            <div>
              <i className='bi bi-x-circle me-2'></i>
              <FormattedMessage {...M.unsubscribed} />
            </div>
            <Badge bg='danger'>{stats.unsubscribed}</Badge>
          </ListGroup.Item>
        </ListGroup>

        {/* Tags Section */}
        <div className='mb-4'>
          <h6 className='filter-subtitle'><FormattedMessage {...M.tags} /></h6>
          {tags.length === 0 ? (
            <p className='text-muted small'>
              <i className='bi bi-tags me-1'></i>
              <FormattedMessage {...M.noTagsYet} />
            </p>
          ) : (
            <ListGroup variant='flush'>
              {tags.map((tag) => {
                const isActive = (filters.tags || []).includes(tag.id)
                return (
                  <ListGroup.Item
                    key={tag.id}
                    action
                    active={isActive}
                    onClick={() => handleTagClick(tag.id)}
                    className='d-flex justify-content-between align-items-center py-1'
                  >
                    <span
                      className='badge rounded-pill'
                      style={{
                        backgroundColor: tag.color,
                        color: getTagContrastColor(tag.color),
                        fontSize: '0.75rem',
                      }}
                    >
                      {tag.name}
                    </span>
                    <Badge bg='secondary' className='ms-2'>
                      {tag.contactCount}
                    </Badge>
                  </ListGroup.Item>
                )
              })}
            </ListGroup>
          )}
        </div>

        {/* Segments Section */}
        <div className='mb-4'>
          <h6 className='filter-subtitle'><FormattedMessage {...M.segments} /></h6>
          {segments.length === 0 ? (
            <p className='text-muted small'>
              <i className='bi bi-grid me-1'></i>
              <FormattedMessage {...M.noSegmentsYet} />
            </p>
          ) : (
            <ListGroup variant='flush'>
              {segments.map((seg) => (
                <ListGroup.Item
                  key={seg.id}
                  action
                  active={filters.segment === seg.id}
                  onClick={() => handleSegmentClick(seg.id)}
                  className='d-flex justify-content-between align-items-center py-1'
                >
                  <div>
                    <i className='bi bi-diagram-3 me-2'></i>
                    <span className='small'>{seg.name}</span>
                  </div>
                  <Badge bg='secondary' className='ms-2'>
                    {seg.contactCount}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <div className='text-center'>
            <button
              className='btn btn-sm btn-outline-secondary'
              onClick={() =>
                onFilterChange({
                  status: 'ALL',
                  search: '',
                  tags: [],
                  segment: '',
                  effectiveYears: [],
                })
              }
            >
              <i className='bi bi-x-circle me-1'></i>
              <FormattedMessage {...M.clearFilters} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
